import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Loader from "../../../../common/Loader";
import { useCmpOrganizationNoun } from "../../../../../contexts/CmpOrganizationNounContext";
import { ActionSuccessDialog } from "../../../../common/dashboard/ActionSuccessDialog";
import { ConfirmDialog } from "../../../../common/dashboard/ConfirmDialog";
import {
  AffinityProgramPartnerActivationStatusEnum,
  AffinityProgramPartnerFieldsFragment,
  AffinityProgramPartnerInput,
  useCreateAffinityProgramPartnerMutation,
  useDeleteAffinityProgramPartnerMutation,
  useGetAllAffinityProgramPartnersForChamberQuery,
  useUpdateAffinityProgramPartnerMutation,
} from "../../../../../graphql/types";
import {
  CalendarDateInput,
  settingsRowCancelButtonClassName,
  settingsRowDeleteButtonClassName,
  settingsRowEditButtonClassName,
} from "../common";
import {
  AffinityProgramDraft,
  hasUnsavedAffinityProgramDraftChanges,
} from "./affinityProgramDirtyState";
import { CALLER_HANDLED_USER_ERROR_CONTEXT } from "../../../../../utils/apolloErrorPolicy";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
} from "../../../../../utils/userFacingError";
import { selectFieldMessage, enterFieldMessage } from "../../../../../utils/fieldMessages";
import { formatCmpLastSavedLabel } from "../../../../../utils/cmpTimeZones";

// Partner calls show their error on this tab instead of the full-page error.
const affinityOperationOptions = { context: CALLER_HANDLED_USER_ERROR_CONTEXT };
const AFFINITY_SAVED_MESSAGE = "Affinity Program Partner saved and published.";
const AFFINITY_SAVED_LIST_NOT_REFRESHED_MESSAGE =
  "Affinity Program Partner saved and published. The list could not refresh. Refresh the page to see the change.";

const maxDescriptionLength = 2000;
const maxLogoSizeBytes = 5 * 1024 * 1024;

const createTodayDateInputValue = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const createBlankDraft = (): AffinityProgramDraft => ({
  companyName: "",
  description: "",
  logo: "",
  startDate: createTodayDateInputValue(),
  title: "",
  redirectLink: "",
});

const createPartnerId = () =>
  `affinity-program-partner-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const dateInputValueToUtcMilli = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day, 12, 0, 0, 0).getTime();
};

const utcMilliToDateInputValue = (utcMilli?: number | null) => {
  if (!utcMilli) {
    return createTodayDateInputValue();
  }

  const date = new Date(utcMilli);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDate = (utcMilli?: number | null) => {
  if (!utcMilli) {
    return "";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(utcMilli));
};

const formatSavedAt = (utcMilli: number | null | undefined, timeZone: string | null | undefined) =>
  utcMilli ? formatCmpLastSavedLabel(utcMilli, timeZone) : "";

const truncateDescription = (description: string) => {
  if (description.length <= 128) {
    return description;
  }

  return `${description.slice(0, 125).trimEnd()}...`;
};

const isPublishedPartner = (
  partner: Pick<AffinityProgramPartnerFieldsFragment, "activationStatus">
) => partner.activationStatus !== AffinityProgramPartnerActivationStatusEnum.Deactivated;

const buildAffinityProgramInput = ({
  id,
  draft,
}: {
  id: string;
  draft: AffinityProgramDraft;
}): AffinityProgramPartnerInput => ({
  id,
  companyName: draft.companyName.trim(),
  title: draft.companyName.trim(),
  description: draft.description.trim(),
  redirectLink: draft.redirectLink,
  logo: draft.logo,
  banner: [],
  // Partners set up here are for internal member tracking only. They never
  // appear in the Chamber Perks App; app partners are added in the CPA dashboard.
  cmpInternalOnly: true,
});

const validateDraft = (draft: AffinityProgramDraft) => {
  if (!draft.companyName.trim()) {
    return enterFieldMessage("Affinity Partner Name");
  }

  if (!draft.description.trim()) {
    return enterFieldMessage("Affinity Partner Description");
  }

  if (draft.description.length > maxDescriptionLength) {
    return "Affinity Partner Description must be 2000 characters or fewer.";
  }

  if (!draft.logo) {
    return selectFieldMessage("Affinity Partner Logo");
  }

  if (!dateInputValueToUtcMilli(draft.startDate)) {
    return selectFieldMessage("Start Date");
  }

  return null;
};

const LogoUploadField = ({
  value,
  onChange,
  onError,
  onReadStateChange,
  disabled = false,
}: {
  value: string;
  onChange: (value: string) => void;
  onError: (message: string) => void;
  onReadStateChange?: (reading: boolean) => void;
  disabled?: boolean;
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleOpenFilePicker = () => {
    if (disabled) {
      return;
    }

    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    if (!["image/png", "image/jpeg"].includes(file.type)) {
      onError("Use a PNG or JPG Affinity Partner Logo image.");
      return;
    }

    if (file.size > maxLogoSizeBytes) {
      onError("Affinity Partner Logo image must be 5MB or smaller.");
      return;
    }

    onReadStateChange?.(true);

    try {
      onChange(await fileToDataUrl(file));
    } catch {
      onError("We could not open this logo image. Try a different PNG or JPG file.");
    } finally {
      onReadStateChange?.(false);
    }
  };

  return (
    <div className="mt-[8px] w-full">
      {/* D:6653-6709: Update Logo and Delete Logo sit inside the dashed box. */}
      {value ? (
        <div className="flex w-full flex-col items-center gap-[10px] rounded-[8px] border border-dashed border-[#C9C9C9] bg-white p-[16px]">
          <div className="flex h-[80px] w-[110px] items-center justify-center rounded-[6px] border border-[#ECECEC] bg-white p-[6px]">
            <img
              src={value}
              alt="Affinity Partner Logo preview"
              className="max-h-full max-w-full object-contain"
            />
          </div>
          <button
            type="button"
            onClick={handleOpenFilePicker}
            disabled={disabled}
            className="inline-flex items-center justify-center rounded-[6px] border-0 bg-[#727272] px-[16px] py-[8px] text-[12px] font-semibold text-white transition hover:bg-[#5E5E5E] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Update Logo
          </button>
          <button
            type="button"
            onClick={() => onChange("")}
            disabled={disabled}
            className="inline-flex items-center justify-center rounded-[6px] border-0 bg-[#E24C4C] px-[16px] py-[8px] text-[12px] font-semibold text-white transition hover:bg-[#D01414] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Delete Logo
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleOpenFilePicker}
          disabled={disabled}
          className="flex h-[190px] w-full flex-col items-center justify-center gap-[10px] rounded-[8px] border border-dashed border-[#C9C9C9] bg-white transition hover:border-cpaGold focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="#727272" strokeWidth="2" strokeLinecap="round" className="h-[26px] w-[26px]" aria-hidden="true">
            <path d="M12 16V4M7 9l5-5 5 5M4 18v2h16v-2" />
          </svg>
          <span className="text-[15px] text-[#727272]">Click Upload Logo</span>
        </button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={handleFileChange}
        disabled={disabled}
      />
    </div>
  );
};

const affinityInputClassName =
  "w-full rounded-[8px] border border-[#ECECEC] bg-white px-[14px] py-[11px] text-[14px] text-[#0A0A0A] outline-none transition placeholder:text-[#9E9E9E] focus:border-cpaGold focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:bg-gray-100";
const affinitySaveButtonClassName =
  "inline-flex items-center justify-center rounded-[8px] border-0 bg-[#E1A731] px-[28px] py-[13px] text-[15px] font-bold text-white transition hover:bg-[#C6912A] disabled:cursor-not-allowed disabled:opacity-50";

const AffinityProgramForm = ({
  draft,
  onChange,
  onSubmit,
  onReset,
  submitting,
  error,
  variant,
  lastSavedLabel,
  onLogoReadStateChange,
  onLogoError,
}: {
  draft: AffinityProgramDraft;
  onChange: (draft: AffinityProgramDraft) => void;
  onSubmit: () => void | Promise<void>;
  onReset?: () => void;
  submitting: boolean;
  error: string | null;
  variant: "create" | "edit";
  lastSavedLabel?: string;
  onLogoReadStateChange?: (reading: boolean) => void;
  onLogoError: (message: string) => void;
}) => {
  const helperText =
    variant === "create"
      ? "Enter In Affinity Program Details Below And Click Save."
      : "Edit the Affinity Program details below and click Save.";

  return (
    <div className="flex flex-col gap-[22px]">
      <div className="rounded-[8px] bg-[#F7F7F8] px-[18px] py-[14px] text-[14px] font-bold text-[#0A0A0A]">
        {helperText}
      </div>

      <div className="grid grid-cols-1 gap-[24px] lg:grid-cols-[280px_minmax(0,1fr)]">
        <div className="flex flex-col gap-[4px]">
          <p className="text-[14px] font-semibold text-[#0A0A0A]">
            Affinity Partner Logo
          </p>
          <p className="text-[14px] text-[#727272]">
            Ideally a square PNG or JPG image 5MB in size
          </p>
          <LogoUploadField
            value={draft.logo}
            onChange={(logo) => onChange({ ...draft, logo })}
            onError={onLogoError}
            onReadStateChange={onLogoReadStateChange}
            disabled={submitting}
          />
        </div>

        <div className="flex flex-col gap-[10px]">
          <input
            value={draft.companyName}
            onChange={(event) =>
              onChange({ ...draft, companyName: event.target.value })
            }
            placeholder="Affinity Partner Name As Also Seen On Website"
            aria-label="Affinity Partner Name"
            className={affinityInputClassName}
            disabled={submitting}
          />
          <textarea
            value={draft.description}
            onChange={(event) =>
              onChange({
                ...draft,
                description: event.target.value.slice(0, maxDescriptionLength),
              })
            }
            placeholder="Affinity Partner Description As Also Seen On Website"
            aria-label="Affinity Partner Description"
            className={`${affinityInputClassName} resize-y ${
              variant === "create" ? "h-[170px]" : "h-[160px]"
            }`}
            disabled={submitting}
          />
          <p className="text-[12px] text-[#9E9E9E]">
            {draft.description.length}/{maxDescriptionLength} characters
          </p>
        </div>
      </div>

      {variant === "edit" && (
        <label className="flex max-w-full flex-col gap-[6px]">
          <span className="text-[14px] font-semibold text-[#0A0A0A]">
            Start Date
          </span>
          <span className="text-[14px] text-[#727272]">
            Set up the start date for the Affinity Program Partner
          </span>
          <CalendarDateInput
            mode="fullDate"
            ariaLabel="Affinity Program Partner start date"
            value={draft.startDate}
            onChange={(startDate) => onChange({ ...draft, startDate })}
            disabled={submitting}
            className="mt-[4px]"
          />
        </label>
      )}

      {error && (
        <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {error}
        </p>
      )}

      {variant === "edit" ? (
        <div className="flex flex-wrap justify-end gap-[12px]">
          {onReset && (
            <button
              type="button"
              onClick={onReset}
              disabled={submitting}
              className="inline-flex items-center justify-center rounded-[8px] border border-[#9E9E9E] bg-white px-[34px] py-[13px] text-[14px] font-semibold text-[#0A0A0A] transition hover:bg-[#F7F7F8] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Reset
            </button>
          )}
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className={affinitySaveButtonClassName}
          >
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-end gap-[8px]">
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className={affinitySaveButtonClassName}
          >
            {submitting ? "Saving..." : "Save"}
          </button>
          {lastSavedLabel && (
            <p className="text-[14px] text-[#727272]">{lastSavedLabel}</p>
          )}
        </div>
      )}
    </div>
  );
};

const DeleteAffinityProgramPartnerModal = ({
  partner,
  deleting,
  onCancel,
  onDelete,
}: {
  partner: AffinityProgramPartnerFieldsFragment;
  deleting: boolean;
  onCancel: () => void;
  onDelete: () => void | Promise<void>;
}) => (
  <ConfirmDialog
    open
    variant="destructive"
    title="Delete Affinity Program Partner"
    message={`Are you sure you want to delete the Affinity Program Partner ${partner.companyName}?`}
    confirmLabel="Delete"
    loading={deleting}
    onConfirm={() => void onDelete()}
    onClose={onCancel}
  />
);

const AffinityProgramSetupTab = ({
  chamberId,
  lastSavedLabel,
  timeZone,
  onDirtyChange,
  onBusyChange,
}: {
  chamberId: string;
  lastSavedLabel: string;
  /** Chamber time zone for the Last Saved stamp. */
  timeZone?: string | null;
  /**
   * B15 guardrail: reports draft dirtiness (non-blank create draft, or an
   * edit draft that differs from its source partner) so the parent's
   * UnsavedChangesPrompt route guard also covers this tab's unsaved work.
   */
  onDirtyChange?: (dirty: boolean) => void;
  onBusyChange?: (busy: boolean) => void;
}) => {
  const noun = useCmpOrganizationNoun();
  const [createDraftBaseline, setCreateDraftBaseline] =
    useState<AffinityProgramDraft>(createBlankDraft);
  const [createDraft, setCreateDraft] = useState<AffinityProgramDraft>(() =>
    ({ ...createDraftBaseline })
  );
  const [createError, setCreateError] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<AffinityProgramDraft | null>(null);
  const [editDraftBaseline, setEditDraftBaseline] =
    useState<AffinityProgramDraft | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [editingPartnerId, setEditingPartnerId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<AffinityProgramPartnerFieldsFragment | null>(null);
  const [lastAffinitySaveAtUtcMilli, setLastAffinitySaveAtUtcMilli] =
    useState<number | null>(null);
  const [activeLogoReadCount, setActiveLogoReadCount] = useState(0);
  // One design pop-up per completed or failed action (replaces toasts).
  const [feedback, setFeedback] = useState<{
    message: string;
    tone: "success" | "error";
  } | null>(null);
  const showError = useCallback(
    (message: string) => setFeedback({ message, tone: "error" }),
    []
  );

  const { data, loading, error, refetch } =
    useGetAllAffinityProgramPartnersForChamberQuery({
      variables: { chamberId },
      skip: !chamberId,
      fetchPolicy: "network-only",
      ...affinityOperationOptions,
    });

  const [createAffinityProgramPartner, { loading: creating }] =
    useCreateAffinityProgramPartnerMutation(affinityOperationOptions);
  const [updateAffinityProgramPartner, { loading: updating }] =
    useUpdateAffinityProgramPartnerMutation(affinityOperationOptions);
  const [deleteAffinityProgramPartner, { loading: deleting }] =
    useDeleteAffinityProgramPartnerMutation(affinityOperationOptions);

  // A saved change must never show a failure pop-up because the list
  // refresh after it failed.
  const refreshPartnersAfterSave = async () => {
    try {
      await refetch();
      return true;
    } catch {
      return false;
    }
  };

  const partners = useMemo(
    () => data?.getAllAffinityProgramPartnersForChamber || [],
    [data?.getAllAffinityProgramPartnersForChamber]
  );
  const createLastSavedLabel =
    formatSavedAt(lastAffinitySaveAtUtcMilli, timeZone) || lastSavedLabel;

  // Keep stable local baselines. A background partner refresh must not move an
  // open editor's saved reference and create a false route warning.
  const createDraftIsDirty = hasUnsavedAffinityProgramDraftChanges({
    baseline: createDraftBaseline,
    current: createDraft,
  });
  const editDraftIsDirty =
    !!editDraft &&
    !!editDraftBaseline &&
    hasUnsavedAffinityProgramDraftChanges({
      baseline: editDraftBaseline,
      current: editDraft,
    });
  const affinityDraftIsDirty =
    createDraftIsDirty || editDraftIsDirty || activeLogoReadCount > 0;
  const affinityIsBusy = activeLogoReadCount > 0 || creating || updating;

  const handleLogoReadStateChange = useCallback((reading: boolean) => {
    setActiveLogoReadCount((currentCount) =>
      reading ? currentCount + 1 : Math.max(0, currentCount - 1)
    );
  }, []);

  useEffect(() => {
    onDirtyChange?.(affinityDraftIsDirty);
  }, [affinityDraftIsDirty, onDirtyChange]);

  useEffect(
    () => () => {
      onDirtyChange?.(false);
    },
    [onDirtyChange],
  );

  useEffect(() => {
    onBusyChange?.(affinityIsBusy);
  }, [affinityIsBusy, onBusyChange]);

  useEffect(
    () => () => {
      onBusyChange?.(false);
    },
    [onBusyChange]
  );

  const handleCreate = async () => {
    const validationError = validateDraft(createDraft);

    if (validationError) {
      setCreateError(validationError);
      return;
    }

    const activationStartDateUtcMilli = dateInputValueToUtcMilli(
      createDraft.startDate
    );

    if (!activationStartDateUtcMilli) {
      setCreateError(selectFieldMessage("Start Date"));
      return;
    }

    try {
      setCreateError(null);
      const result = await createAffinityProgramPartner({
        variables: {
          chamberId,
          activationStartDateUtcMilli,
          input: buildAffinityProgramInput({
            id: createPartnerId(),
            draft: createDraft,
          }),
        },
      });

      if (!result.data?.createAffinityProgramPartner.id) {
        throw new Error("Affinity Program Partner create returned no id.");
      }

      const nextBlankDraft = createBlankDraft();
      setCreateDraftBaseline(nextBlankDraft);
      setCreateDraft({ ...nextBlankDraft });
      setLastAffinitySaveAtUtcMilli(Date.now());
      const refreshed = await refreshPartnersAfterSave();
      setFeedback({
        message: refreshed
          ? AFFINITY_SAVED_MESSAGE
          : AFFINITY_SAVED_LIST_NOT_REFRESHED_MESSAGE,
        tone: "success",
      });
    } catch (mutationError: unknown) {
      const message = getUserFacingErrorText(
        mutationError,
        buildStaffErrorFallback("save the Affinity Program Partner")
      );
      setCreateError(message);
      showError(message);
    }
  };

  const handleStartEdit = (partner: AffinityProgramPartnerFieldsFragment) => {
    const nextDraft = {
      companyName: partner.companyName,
      description: partner.description,
      logo: partner.logo,
      startDate: utcMilliToDateInputValue(partner.activationStartDateUtcMilli),
      title: partner.title,
      redirectLink: partner.redirectLink,
    };
    setEditingPartnerId(partner.id);
    setEditDraftBaseline(nextDraft);
    setEditDraft({ ...nextDraft });
    setEditError(null);
  };

  const handleCancelEdit = () => {
    setEditingPartnerId(null);
    setEditDraft(null);
    setEditDraftBaseline(null);
    setEditError(null);
  };

  const handleResetEdit = () => {
    const partner = partners.find(({ id }) => id === editingPartnerId);

    if (!partner) {
      return;
    }

    handleStartEdit(partner);
  };

  const handleUpdate = async () => {
    if (!editDraft || !editingPartnerId) {
      return;
    }

    const validationError = validateDraft(editDraft);

    if (validationError) {
      setEditError(validationError);
      return;
    }

    const activationStartDateUtcMilli = dateInputValueToUtcMilli(
      editDraft.startDate
    );

    if (!activationStartDateUtcMilli) {
      setEditError(selectFieldMessage("Start Date"));
      return;
    }

    try {
      setEditError(null);
      const result = await updateAffinityProgramPartner({
        variables: {
          id: editingPartnerId,
          activationStartDateUtcMilli,
          input: buildAffinityProgramInput({
            id: editingPartnerId,
            draft: editDraft,
          }),
        },
      });

      if (!result.data?.updateAffinityProgramPartner.id) {
        throw new Error("Affinity Program Partner update returned no id.");
      }

      handleCancelEdit();
      setLastAffinitySaveAtUtcMilli(Date.now());
      const refreshed = await refreshPartnersAfterSave();
      setFeedback({
        message: refreshed
          ? AFFINITY_SAVED_MESSAGE
          : AFFINITY_SAVED_LIST_NOT_REFRESHED_MESSAGE,
        tone: "success",
      });
    } catch (mutationError: unknown) {
      const message = getUserFacingErrorText(
        mutationError,
        buildStaffErrorFallback("update the Affinity Program Partner")
      );
      setEditError(message);
      showError(message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      const result = await deleteAffinityProgramPartner({
        variables: { id: deleteTarget.id },
      });

      if (!result.data?.deleteAffinityProgramPartner.deleted) {
        throw new Error("Affinity Program Partner delete was not confirmed.");
      }

      if (editingPartnerId === deleteTarget.id) {
        handleCancelEdit();
      }

      setDeleteTarget(null);
      setLastAffinitySaveAtUtcMilli(Date.now());
      const refreshed = await refreshPartnersAfterSave();
      setFeedback({
        message: refreshed
          ? "Affinity Program Partner deleted."
          : "Affinity Program Partner deleted. The list could not refresh. Refresh the page to see the change.",
        tone: "success",
      });
    } catch (mutationError: unknown) {
      const message = getUserFacingErrorText(
        mutationError,
        buildStaffErrorFallback("delete the Affinity Program Partner")
      );
      // The confirm stays open for a retry; the error pop-up opens above it.
      showError(message);
    }
  };

  const editingPartner = partners.find(({ id }) => id === editingPartnerId);
  const affinityHeaderCellClassName =
    "px-[8px] py-[14px] text-[13px] font-bold text-[#0A0A0A]";
  const affinityBodyCellClassName =
    "px-[8px] py-[18px] align-top text-[14px] text-[#0A0A0A]";

  // Layout follows D:6640-6731. Copy changed Oct 7 2026 (Moeen): these
  // partners are internal only, so they are never published to the website
  // or the Chamber Perks App.
  return (
    <section className="flex flex-col gap-[22px] rounded-[12px] border border-[#ECECEC] bg-white p-7 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
      <div>
        <h2 className="text-[18px] font-semibold text-[#0A0A0A]">
          Create Affinity Program Set Up
        </h2>
        <p className="mt-[4px] text-[14px] text-[#0A0A0A]">
          Create and manage your {noun.possessive} affinity programs for internal
          member tracking. Link members to these programs on each member profile
          to see who is enrolled. Programs added here are not shown on your{" "}
          {noun.singular} website or in the Chamber Perks App{"™"}. To list a
          program in the Chamber Perks App{"™"}, add it in your Chamber Perks
          App{"™"} dashboard.
        </p>
      </div>

      <AffinityProgramForm
        draft={createDraft}
        onChange={(draft) => {
          setCreateDraft(draft);
          setCreateError(null);
        }}
        onSubmit={handleCreate}
        submitting={creating}
        error={createError}
        variant="create"
        lastSavedLabel={createLastSavedLabel}
        onLogoReadStateChange={handleLogoReadStateChange}
        onLogoError={showError}
      />

      <div className="border-t border-[#ECECEC] pt-[22px]">
        <h2 className="text-[18px] font-semibold text-[#0A0A0A]">
          Manage Affinity Program Partners
        </h2>
        <p className="mt-[4px] text-[14px] text-[#0A0A0A]">
          Update descriptions and start date for each Affinity Program Partner
          below. These partners are for internal member tracking only.
        </p>
      </div>

      {loading ? (
        <div className="flex min-h-[220px] items-center justify-center">
          <Loader />
        </div>
      ) : error ? (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          <p className="font-semibold">
            Unable to load Affinity Program Partners.
          </p>
          <p className="mt-1">
            {getUserFacingErrorText(
              error,
              buildStaffErrorFallback("load the Affinity Program Partners")
            )}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-4 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100"
          >
            Try again
          </button>
        </div>
      ) : partners.length === 0 ? (
        <div className="rounded-lg bg-gray-50 px-5 py-4 text-sm text-gray-600">
          No Affinity Program Partners have been published yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[805px] table-fixed border-separate border-spacing-0 text-left">
            <thead>
              <tr className="bg-[#F7F7F8]">
                <th className={`w-[23%] rounded-l-[8px] !px-[16px] ${affinityHeaderCellClassName}`}>
                  Affinity Program Partner
                </th>
                <th className={`w-[14%] ${affinityHeaderCellClassName}`}>
                  Start Date
                </th>
                <th className={`w-[31%] ${affinityHeaderCellClassName}`}>
                  Description
                </th>
                <th className={`w-[15%] ${affinityHeaderCellClassName}`}>
                  Published
                </th>
                <th className={`sticky right-0 z-[1] w-[17%] rounded-r-[8px] bg-[#F7F7F8] shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)] ${affinityHeaderCellClassName}`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {partners.map((partner) => {
                const published = isPublishedPartner(partner);
                const isEditing = editingPartnerId === partner.id;

                return (
                  <tr key={partner.id}>
                    <td className={`border-b border-[#ECECEC] !px-[16px] ${affinityBodyCellClassName}`}>
                      <span className="flex gap-[8px]">
                        <span aria-hidden="true" className="text-[#C9C9C9]">
                          ⋮⋮
                        </span>
                        {partner.companyName}
                      </span>
                    </td>
                    <td className={`border-b border-[#ECECEC] ${affinityBodyCellClassName}`}>
                      {formatDate(partner.activationStartDateUtcMilli)}
                    </td>
                    <td className={`border-b border-[#ECECEC] ${affinityBodyCellClassName}`}>
                      {truncateDescription(partner.description)}
                    </td>
                    <td className={`border-b border-[#ECECEC] ${affinityBodyCellClassName}`}>
                      <span
                        className={`inline-flex items-center gap-[8px] ${
                          published ? "text-[#10B981]" : "text-[#E24C4C]"
                        }`}
                      >
                        <span
                          className={`h-[7px] w-[7px] rounded-full ${
                            published ? "bg-[#10B981]" : "bg-[#E24C4C]"
                          }`}
                        />
                        {published ? "Published" : "Not Published"}
                      </span>
                    </td>
                    <td className={`sticky right-0 border-b border-[#ECECEC] bg-white shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)] ${affinityBodyCellClassName}`}>
                      <div className="flex gap-[8px]">
                        {isEditing ? (
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            disabled={updating}
                            className={settingsRowCancelButtonClassName}
                          >
                            Cancel
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => handleStartEdit(partner)}
                              disabled={updating || deleting}
                              className={settingsRowEditButtonClassName}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(partner)}
                              disabled={updating || deleting}
                              className={settingsRowDeleteButtonClassName}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editingPartner && editDraft && (
        <AffinityProgramForm
          draft={editDraft}
          onChange={(draft) => {
            setEditDraft(draft);
            setEditError(null);
          }}
          onSubmit={handleUpdate}
          onReset={handleResetEdit}
          submitting={updating}
          error={editError}
          variant="edit"
          onLogoReadStateChange={handleLogoReadStateChange}
          onLogoError={showError}
        />
      )}

      {deleteTarget && (
        <DeleteAffinityProgramPartnerModal
          partner={deleteTarget}
          deleting={deleting}
          onCancel={() => setDeleteTarget(null)}
          onDelete={handleDelete}
        />
      )}

      <ActionSuccessDialog
        message={feedback?.message}
        tone={feedback?.tone}
        onClose={() => setFeedback(null)}
      />
    </section>
  );
};

export default AffinityProgramSetupTab;
